import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { HomeIcon, Users, Heart, TrendingUp, Phone, MapPin } from 'lucide-react';
import './Programs.css';

const Programs = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const programs = [
    {
      icon: HomeIcon,
      title: 'Affordable Housing',
      description: 'By-the-room pricing in a shared, supportive environment that prioritizes your dignity and independence.'
    },
    {
      icon: Users,
      title: 'Structure & Accountability',
      description: 'Clear expectations and community guidelines designed to support long-term success and personal growth.'
    },
    {
      icon: Heart,
      title: 'Access to Resources',
      description: 'Direct connections to employment opportunities, counseling services, veteran support, and reentry programs.'
    }
  ];

  const eligibility = [
    'Ready to take the next step in your journey',
    'Committed to improving your situation',
    'Able to follow structured community guidelines',
    'Seeking stable, supportive housing',
    'Willing to respect others in a shared environment'
  ];

  return (
    <section id="what-we-provide" className="programs" ref={ref}>
      <div className="programs-container">
        <motion.div
          className="programs-header"
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <span className="section-label">What We Provide</span>
          <h2 className="section-title">
            Comprehensive support for
            <br />
            <span className="highlight">your new beginning</span>
          </h2>
        </motion.div>

        <div className="programs-grid">
          {programs.map((program, index) => {
            const Icon = program.icon;
            return (
              <motion.div
                key={index}
                className="program-card"
                initial={{ opacity: 0, y: 50 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 + index * 0.15 }}
                whileHover={{ scale: 1.03 }}
              >
                <div className="program-icon-wrapper">
                  <Icon className="program-icon" size={36} />
                </div>
                <h3 className="program-title">{program.title}</h3>
                <p className="program-description">{program.description}</p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          className="eligibility-section"
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <div className="eligibility-content">
            <div className="eligibility-text">
              <h3>Who This Program Is For</h3>
              <p>
                This program is designed for individuals who are ready to rebuild their
                lives with structure, support, and community.
              </p>
              <ul className="eligibility-list">
                {eligibility.map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.5, delay: 1 + index * 0.1 }}
                  >
                    <span className="checkmark">✓</span>
                    {item}
                  </motion.li>
                ))}
              </ul>
            </div>
            <motion.div
              className="cta-card"
              whileHover={{ scale: 1.02, y: -5 }}
            >
              <TrendingUp className="cta-icon" size={48} />
              <h4>Ready to Apply?</h4>
              <p>Take the first step towards a better future. Our team is here to help you every step of the way.</p>
              <motion.a
                href="/apply"
                className="cta-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Start Your Application
              </motion.a>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Programs;
